import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { Category, CategoryKind, ListResponse } from './types'

const ROOT = '/categories'
const KEY = ['categories'] as const

// System-seeded category id (mirrors apps/worker/src/lib/system.ts). The
// operation form special-cases it: picking «Зарплата» switches the form into
// salary-payout mode (employee + accrual instead of a free counterparty).
export const SALARY_CATEGORY_ID = '01HSYSTEMSALARY00000000000A'

export function useCategories(filter: { kind?: CategoryKind } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, filter],
    queryFn: () =>
      api<ListResponse<Category>>(`${ROOT}${buildQuery({ kind: filter.kind })}`).then(
        (r) => r.items,
      ),
  })
}

export type CreateCategoryInput = {
  kind: CategoryKind
  name: string
  parentId?: string | null
  code?: string | null
}

export function useCreateCategory() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCategoryInput) =>
      api<Category>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type UpdateCategoryInput = {
  id: string
  patch: Partial<{
    name: string
    parentId: string | null
    code: string | null
    isArchived: boolean
  }>
}

export function useUpdateCategory() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateCategoryInput) =>
      api<Category>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteCategory() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
